// Domains list page (MIS-809 / Task 28; setup-status column FF-23 / MIS-1006).
//
// Fetches the active tenant's domains via the typed RPC client and renders them
// in the shared DataTable (Host, Brand, Status badge, Setup readiness badge,
// Last activity, Actions: view / edit / delete). The Setup column comes from
// ONE aggregate `GET /api/setup-status` query (`useSetupStatuses()` — never a
// per-row fetch) and the All/Ready/Incomplete pills filter on it. "Add Domain"
// opens the AddDomainDialog. Delete opens a confirm dialog, then DELETEs and
// refetches. The list is always fetched fresh from the server (React Query) —
// never cached in local/component state — and refetches automatically when the
// active tenant changes (useApi re-injects the X-Tenant-Id header, changing the
// query result).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { AddDomainDialog } from '@/components/domains/AddDomainDialog';
import { CloneConfigDialog } from '@/components/domains/CloneConfigDialog';
import { DomainStatusBadge } from '@/components/domains/DomainStatusBadge';
import { formatTimestamp } from '@/components/domains/format';
import {
  type ReadinessFilter,
  ReadinessFilterPills,
  SetupStatusBadge,
  useSetupStatuses,
} from '@/components/domains/SetupStatusBadge';
import type { Domain } from '@/components/domains/types';
import { Button } from '@/components/ui/Button';
import { DataTable } from '@/components/ui/DataTable';
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { useToast } from '@/components/ui/toast';
import { useApi } from '@/lib/api';
import { useTenantContext } from '@/lib/auth';

export const Route = createFileRoute('/_authenticated/domains/')({
  component: DomainsListPage,
});

function DomainsListPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeTenantId } = useTenantContext();

  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Domain | null>(null);
  // FF-24: which domain's config is being cloned FROM (row menu action).
  const [pendingCloneConfig, setPendingCloneConfig] = useState<Domain | null>(null);
  const [readinessFilter, setReadinessFilter] = useState<ReadinessFilter>('all');

  const {
    data: domains,
    isLoading,
    isError,
  } = useQuery({
    // Keyed by tenant so switching workspaces refetches a fresh list.
    queryKey: ['domains', activeTenantId],
    queryFn: async () => {
      const res = await api.api.domains.$get();
      if (!res.ok) throw new Error('Failed to load domains');
      const data = await res.json();
      return data.domains;
    },
  });

  // ONE aggregate setup-status fetch for the whole table (FF-23) — the badge
  // column and the readiness pills both read from it; never per-row queries.
  const { data: setupStatuses } = useSetupStatuses();

  const statusByDomain = useMemo(() => {
    const map = new Map<number, NonNullable<typeof setupStatuses>[number]>();
    for (const s of setupStatuses ?? []) map.set(s.domainId, s);
    return map;
  }, [setupStatuses]);

  const filteredDomains = useMemo(() => {
    const rows = domains ?? [];
    if (readinessFilter === 'all') return rows;
    return rows.filter((d) => {
      // Not-yet-loaded/unknown statuses count as incomplete, not ready.
      const ready = statusByDomain.get(d.id)?.ready === true;
      return readinessFilter === 'ready' ? ready : !ready;
    });
  }, [domains, readinessFilter, statusByDomain]);

  const deleteMutation = useMutation({
    mutationFn: async (domainId: number) => {
      const res = await api.api.domains[':domainId'].$delete({
        param: { domainId: String(domainId) },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['domains'] });
      toast({ title: 'Domain removed', variant: 'success' });
      setPendingDelete(null);
    },
    onError: (err: Error) => {
      toast({ title: 'Could not remove domain', description: err.message, variant: 'destructive' });
    },
  });

  const columns = useMemo<ColumnDef<Domain, unknown>[]>(
    () => [
      {
        accessorKey: 'host',
        header: 'Host',
        cell: ({ row }) => (
          <Link
            to="/domains/$domainId"
            params={{ domainId: String(row.original.id) }}
            className="font-medium text-gray-900 hover:underline"
          >
            {row.original.host}
          </Link>
        ),
      },
      {
        accessorKey: 'brandName',
        header: 'Brand',
        cell: ({ row }) => row.original.brandName ?? <span className="text-gray-400">—</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => <DomainStatusBadge domain={row.original} />,
      },
      {
        id: 'setup',
        header: 'Setup',
        cell: ({ row }) => {
          const status = statusByDomain.get(row.original.id);
          return status ? (
            <SetupStatusBadge status={status} />
          ) : (
            <span className="text-gray-400">—</span>
          );
        },
      },
      {
        id: 'lastActivity',
        header: 'Last activity',
        cell: ({ row }) => {
          const ts = row.original.activatedAt ?? row.original.updatedAt ?? row.original.createdAt;
          return <span className="text-gray-500">{ts ? formatTimestamp(ts) : '—'}</span>;
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            <Link to="/domains/$domainId" params={{ domainId: String(row.original.id) }}>
              <Button variant="ghost" size="sm">
                View
              </Button>
            </Link>
            <Link to="/domains/$domainId" params={{ domainId: String(row.original.id) }}>
              <Button variant="ghost" size="sm">
                Edit
              </Button>
            </Link>
            {/* FF-24: copy this domain's setup onto another domain. */}
            <Button variant="ghost" size="sm" onClick={() => setPendingCloneConfig(row.original)}>
              Clone config
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:bg-red-50"
              onClick={() => setPendingDelete(row.original)}
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [statusByDomain],
  );

  return (
    <div className="space-y-6 p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Domains</h1>
          <p className="text-sm text-gray-500">Custom hostnames for this workspace.</p>
        </div>
        <Button onClick={() => setAddOpen(true)}>Add Domain</Button>
      </div>

      <ReadinessFilterPills value={readinessFilter} onChange={setReadinessFilter} />

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading domains…</p>
      ) : isError ? (
        <p className="text-sm text-red-600">Failed to load domains. Try again.</p>
      ) : (domains ?? []).length === 0 ? (
        // FF-21: zero domains at all → point at the funnel wizard (it starts at
        // the domain step). Filter-empty states keep the plain table message.
        <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-[var(--border-soft)] p-10 text-center">
          <p className="text-sm text-gray-500">
            No domains yet — a funnel starts with one. The wizard walks you from registration to
            live ads.
          </p>
          <Link to="/funnels/new">
            <Button>Create your first funnel</Button>
          </Link>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filteredDomains}
          emptyMessage="No domains match this filter."
        />
      )}

      <AddDomainDialog open={addOpen} onOpenChange={setAddOpen} />

      {/* Mounted per-source so the dialog's state keys off the chosen row. */}
      {pendingCloneConfig !== null && (
        <CloneConfigDialog
          sourceDomainId={pendingCloneConfig.id}
          open
          onOpenChange={(isOpen) => !isOpen && setPendingCloneConfig(null)}
        />
      )}

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        ariaLabel="Confirm delete domain"
      >
        <DialogHeader>
          <DialogTitle>Remove domain</DialogTitle>
          <DialogDescription>
            This revokes the Cloudflare hostname and deletes its settings. This can't be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-gray-700">
            Remove <span className="font-mono font-medium">{pendingDelete?.host}</span>?
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPendingDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
          >
            {deleteMutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}
